import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import founderMichael from "../assets/founder-michael.webp";
import founderThomas from "../assets/founder-thomas.webp";
import founderLawrence from "../assets/founder-lawrence.webp";
import founderAlastair from "../assets/founder-alastair.webp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ContentContext = createContext(null);

// Founder headshots keyed by initials — used as a resilient fallback even if CMS content omits photos.
export const FOUNDER_PHOTOS = { AB: founderAlastair, LH: founderLawrence, TP: founderThomas, MS: founderMichael };

// Fallback defaults so the site always renders even before the CMS responds.
export const DEFAULT_CONTENT = {
  hero: {
    eyebrow: "Independent AI governance & oversight",
    title: "See, control and evidence high-risk AI interactions.",
    subtitle:
      "norse3 is an independent control layer for AI and human conversations. It applies your policies in real time, intervenes when required and creates a reviewable audit trail for risk, security and compliance teams.",
    primaryCta: "Book a walkthrough",
    secondaryCta: "See how it works",
  },
  contact: { email: "info@norse3.com", location: "United Kingdom" },
  pages: {
    platform: { eyebrow: "Platform", title: "Three components. One control and evidence layer.", subtitle: "Bifröst Relay, Aegis Engine and Heimdall Console work together to identify risk, apply your policy and keep humans in control." },
    logberg: { eyebrow: "Lögberg", title: "Screen regulated conversations. Keep reviewers in control.", subtitle: "Lögberg is built for mortgage firms, regulated brokers and QA and compliance teams. It screens regulated calls, prioritises material risks for human review and retains linked evidence. FCA-aligned and reviewer-controlled — never FCA-approved." },
    insights: { eyebrow: "Insights", title: "Perspectives on runtime AI risk and oversight.", subtitle: "How manipulation of AI systems actually works, why it matters for regulated firms, and what independent runtime controls change." },
  },
  insights: [],
  team: [
    { name: "Alastair Band", role: "Co-Founder · CEO & Product", initials: "AB", linkedin: "https://www.linkedin.com/in/alastairband/", photo: founderAlastair },
    { name: "Lawrence Hui", role: "Co-Founder · CTO", initials: "LH", linkedin: "https://www.linkedin.com/in/ka-chun-hui/", photo: founderLawrence },
    { name: "Thomas Pritchard", role: "Co-Founder · CRO", initials: "TP", linkedin: "https://www.linkedin.com/in/thomas-pritchard-chcp2222/", photo: founderThomas },
    { name: "Michael Simmonds", role: "Co-Founder · COO", initials: "MS", linkedin: "https://www.linkedin.com/in/michaelsimmonds/", photo: founderMichael },
  ],
};

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cms/content`);
      setContent({ ...DEFAULT_CONTENT, ...res.data });
    } catch (e) {
      setContent(DEFAULT_CONTENT);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <ContentContext.Provider value={{ content, loaded, refresh, setContent }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) return { content: DEFAULT_CONTENT, loaded: true, refresh: () => {}, setContent: () => {} };
  return ctx;
}
